import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Input } from './ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  CheckCircle2, XCircle, AlertCircle, FileText, ClipboardCheck, 
  GraduationCap, Search, RefreshCw, ChevronDown, ChevronUp,
  Users, TrendingUp, Shield, Heart, Award, BookOpen
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import API from '@/config/api';
// API imported from config;

const FormComplianceDashboard = ({ getAuthHeaders, onOpenForm }) => {
  const [loading, setLoading] = useState(true);
  const [complianceData, setComplianceData] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('staff_name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [expandedStaff, setExpandedStaff] = useState(null);

  useEffect(() => {
    loadComplianceData();
  }, []);

  const loadComplianceData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/staff/forms-compliance`, getAuthHeaders());
      setComplianceData(response.data);
    } catch (error) {
      console.error('Error loading compliance data:', error);
      toast.error('Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'complete':
        return <CheckCircle2 className="w-4 h-4 text-green-600" />;
      case 'partial':
        return <AlertCircle className="w-4 h-4 text-yellow-600" />;
      case 'missing':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'complete':
        return <Badge className="bg-green-100 text-green-700">Complete</Badge>;
      case 'partial':
        return <Badge className="bg-yellow-100 text-yellow-700">Partial</Badge>;
      case 'missing':
        return <Badge className="bg-red-100 text-red-700">Missing</Badge>;
      default:
        return null;
    }
  };

  const filteredStaff = complianceData?.staff_compliance?.filter(staff => 
    staff.staff_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    staff.staff_email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    staff.position.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  const sortedStaff = [...filteredStaff].sort((a, b) => {
    let aVal, bVal;
    
    switch (sortField) {
      case 'staff_name':
        aVal = a.staff_name.toLowerCase();
        bVal = b.staff_name.toLowerCase();
        break;
      case 'position':
        aVal = a.position.toLowerCase();
        bVal = b.position.toLowerCase();
        break;
      case 'compliance':
        const aComplete = (a.staff_record.complete ? 1 : 0) + 
                         (a.orientation.complete ? 1 : 0) + 
                         (a.continuing_education.complete ? 1 : 0);
        const bComplete = (b.staff_record.complete ? 1 : 0) + 
                         (b.orientation.complete ? 1 : 0) + 
                         (b.continuing_education.complete ? 1 : 0);
        aVal = aComplete;
        bVal = bComplete;
        break;
      default:
        aVal = a.staff_name.toLowerCase();
        bVal = b.staff_name.toLowerCase();
    }
    
    if (sortDirection === 'asc') {
      return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    } else {
      return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
    }
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getStaffComplianceLevel = (staff) => {
    // Count all 9 compliance items
    const completeCount = 
      (staff.staff_record?.complete ? 1 : 0) + 
      (staff.orientation?.complete ? 1 : 0) + 
      (staff.continuing_education?.complete ? 1 : 0) +
      (staff.cpr?.complete ? 1 : 0) +
      (staff.mandated_reporter?.complete ? 1 : 0) +
      (staff.high_school_diploma?.complete ? 1 : 0) +
      (staff.registry_certificate?.complete ? 1 : 0) +
      (staff.background_check?.complete ? 1 : 0) +
      (staff.health_report?.complete ? 1 : 0);
    
    if (completeCount === 9) return 'complete';
    if (completeCount > 0) return 'partial';
    return 'missing';
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const summary = complianceData?.summary || {};

  return (
    <div className="space-y-6" data-testid="forms-compliance-dashboard">
      {/* Summary Cards - Top Row */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="md:col-span-1">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Overall</p>
                <p className="text-3xl font-bold">{summary.overall_compliance_percentage}%</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <Progress value={summary.overall_compliance_percentage} className="mt-3" />
          </CardContent>
        </Card>
        
        {/* Forms Section */}
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground mb-1">Staff Records</p>
            <p className="text-xl font-bold">{summary.staff_record_complete}/{summary.total_staff}</p>
            <div className="h-1 w-full bg-gray-200 rounded mt-2">
              <div 
                className="h-1 bg-green-500 rounded" 
                style={{width: `${(summary.staff_record_complete / summary.total_staff) * 100}%`}}
              />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground mb-1">Orientation</p>
            <p className="text-xl font-bold">{summary.orientation_complete}/{summary.total_staff}</p>
            <div className="h-1 w-full bg-gray-200 rounded mt-2">
              <div 
                className="h-1 bg-purple-500 rounded" 
                style={{width: `${(summary.orientation_complete / summary.total_staff) * 100}%`}}
              />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground mb-1">Continuing Ed</p>
            <p className="text-xl font-bold">{summary.continuing_education_complete}/{summary.total_staff}</p>
            <div className="h-1 w-full bg-gray-200 rounded mt-2">
              <div 
                className="h-1 bg-orange-500 rounded" 
                style={{width: `${(summary.continuing_education_complete / summary.total_staff) * 100}%`}}
              />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground mb-1">Background Check</p>
            <p className="text-xl font-bold">{summary.background_check_complete}/{summary.total_staff}</p>
            <div className="h-1 w-full bg-gray-200 rounded mt-2">
              <div 
                className="h-1 bg-blue-500 rounded" 
                style={{width: `${(summary.background_check_complete / summary.total_staff) * 100}%`}}
              />
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Documents Section - Second Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <Heart className="w-4 h-4 text-red-500" />
              <p className="text-xs text-muted-foreground">CPR</p>
            </div>
            <p className="text-xl font-bold">{summary.cpr_complete}/{summary.total_staff}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <Shield className="w-4 h-4 text-amber-500" />
              <p className="text-xs text-muted-foreground">Mandated Reporter</p>
            </div>
            <p className="text-xl font-bold">{summary.mandated_reporter_complete}/{summary.total_staff}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <GraduationCap className="w-4 h-4 text-indigo-500" />
              <p className="text-xs text-muted-foreground">HS Diploma</p>
            </div>
            <p className="text-xl font-bold">{summary.high_school_diploma_complete}/{summary.total_staff}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <Award className="w-4 h-4 text-teal-500" />
              <p className="text-xs text-muted-foreground">WI Registry</p>
            </div>
            <p className="text-xl font-bold">{summary.registry_certificate_complete}/{summary.total_staff}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <BookOpen className="w-4 h-4 text-pink-500" />
              <p className="text-xs text-muted-foreground">Health Report</p>
            </div>
            <p className="text-xl font-bold">{summary.health_report_complete}/{summary.total_staff}</p>
          </CardContent>
        </Card>
      </div>

      {/* Staff Compliance Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Staff Form and Document Compliance
            </CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative w-full sm:w-auto">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search staff..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 w-full sm:w-64"
                  data-testid="compliance-search"
                />
              </div>
              <Button variant="outline" size="sm" onClick={loadComplianceData}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg overflow-hidden overflow-x-auto">
            <table className="mobile-cards w-full text-sm md:min-w-[900px]">
              <thead className="bg-muted">
                <tr>
                  <th 
                    className="text-left p-3 cursor-pointer hover:bg-muted/80"
                    onClick={() => handleSort('staff_name')}
                  >
                    <div className="flex items-center gap-1">
                      Staff
                      {sortField === 'staff_name' && (
                        sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                      )}
                    </div>
                  </th>
                  <th className="text-center p-2 text-xs">Record</th>
                  <th className="text-center p-2 text-xs">Orient.</th>
                  <th className="text-center p-2 text-xs">Cont. Ed</th>
                  <th className="text-center p-2 text-xs">CPR</th>
                  <th className="text-center p-2 text-xs">Mandated</th>
                  <th className="text-center p-2 text-xs">HS Dip.</th>
                  <th className="text-center p-2 text-xs">Registry</th>
                  <th className="text-center p-2 text-xs">Bkg Chk</th>
                  <th className="text-center p-2 text-xs">Health</th>
                  <th 
                    className="text-center p-3 cursor-pointer hover:bg-muted/80"
                    onClick={() => handleSort('compliance')}
                  >
                    <div className="flex items-center justify-center gap-1 text-xs">
                      Status
                      {sortField === 'compliance' && (
                        sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedStaff.map((staff, idx) => (
                  <React.Fragment key={staff.staff_id}>
                    <tr 
                      className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 cursor-pointer transition-colors`}
                      onClick={() => setExpandedStaff(expandedStaff === staff.staff_id ? null : staff.staff_id)}
                    >
                      <td className="p-3">
                        <div>
                          <p className="font-medium text-sm">{staff.staff_name}</p>
                          <p className="text-xs text-muted-foreground">{staff.position}</p>
                        </div>
                      </td>
                      <td className="p-2 text-center">
                        {getStatusIcon(staff.staff_record?.status)}
                      </td>
                      <td className="p-2 text-center">
                        {getStatusIcon(staff.orientation?.status)}
                      </td>
                      <td className="p-2 text-center">
                        {getStatusIcon(staff.continuing_education?.status)}
                      </td>
                      <td className="p-2 text-center">
                        {getStatusIcon(staff.cpr?.status)}
                      </td>
                      <td className="p-2 text-center">
                        {getStatusIcon(staff.mandated_reporter?.status)}
                      </td>
                      <td className="p-2 text-center">
                        {getStatusIcon(staff.high_school_diploma?.status)}
                      </td>
                      <td className="p-2 text-center">
                        {getStatusIcon(staff.registry_certificate?.status)}
                      </td>
                      <td className="p-2 text-center">
                        {getStatusIcon(staff.background_check?.status)}
                      </td>
                      <td className="p-2 text-center">
                        {getStatusIcon(staff.health_report?.status)}
                      </td>
                      <td className="p-2 text-center">
                        {getStatusBadge(getStaffComplianceLevel(staff))}
                      </td>
                    </tr>
                    
                    {/* Expanded Details Row */}
                    {expandedStaff === staff.staff_id && (
                      <tr className="bg-blue-50/50">
                        <td colSpan={11} className="p-4">
                          <Tabs defaultValue="forms" className="w-full">
                            <TabsList className="mb-4">
                              <TabsTrigger value="forms">Forms</TabsTrigger>
                              <TabsTrigger value="documents">Documents</TabsTrigger>
                            </TabsList>
                            
                            <TabsContent value="forms">
                              <div className="grid grid-cols-3 gap-4">
                                {/* Staff Record */}
                                <div className="p-3 bg-white rounded-lg border">
                                  <div className="flex items-center justify-between mb-2">
                                    <h4 className="font-medium text-sm flex items-center gap-2">
                                      <FileText className="w-4 h-4" />
                                      Staff Record
                                    </h4>
                                    {getStatusBadge(staff.staff_record?.status)}
                                  </div>
                                  <p className="text-xs text-muted-foreground mb-2">DCF-F-CFS0053</p>
                                  {onOpenForm && (
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onOpenForm(staff, 'staff-record');
                                      }}
                                      data-testid={`open-staff-record-${staff.staff_id}`}
                                    >
                                      {staff.staff_record?.complete ? 'View/Edit' : 'Fill Out'}
                                    </Button>
                                  )}
                                </div>
                                
                                {/* Orientation */}
                                <div className="p-3 bg-white rounded-lg border">
                                  <div className="flex items-center justify-between mb-2">
                                    <h4 className="font-medium text-sm flex items-center gap-2">
                                      <ClipboardCheck className="w-4 h-4" />
                                      Orientation
                                    </h4>
                                    {getStatusBadge(staff.orientation?.status)}
                                  </div>
                                  <p className="text-xs text-muted-foreground mb-1">DCF-F-CFS2026</p>
                                  <Progress value={staff.orientation?.percentage || 0} className="h-2 mb-2" />
                                  <p className="text-xs text-muted-foreground mb-2">
                                    {staff.orientation?.completed_items || 0} of {staff.orientation?.total_items || 19} items
                                  </p>
                                  {onOpenForm && (
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onOpenForm(staff, 'orientation');
                                      }}
                                      data-testid={`open-orientation-${staff.staff_id}`}
                                    >
                                      {staff.orientation?.complete ? 'View/Edit' : 'Fill Out'}
                                    </Button>
                                  )}
                                </div>
                                
                                {/* Continuing Education */}
                                <div className="p-3 bg-white rounded-lg border">
                                  <div className="flex items-center justify-between mb-2">
                                    <h4 className="font-medium text-sm flex items-center gap-2">
                                      <GraduationCap className="w-4 h-4" />
                                      Continuing Ed
                                    </h4>
                                    {getStatusBadge(staff.continuing_education?.status)}
                                  </div>
                                  <p className="text-xs text-muted-foreground mb-1">DCF-F-CFS0053A ({staff.continuing_education?.year})</p>
                                  <p className="text-sm font-medium mb-2">
                                    {staff.continuing_education?.entries_count || 0} entries • {staff.continuing_education?.total_hours || 0} hours
                                  </p>
                                  {onOpenForm && (
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onOpenForm(staff, 'continuing-education');
                                      }}
                                      data-testid={`open-ce-${staff.staff_id}`}
                                    >
                                      {staff.continuing_education?.complete ? 'View/Edit' : 'Fill Out'}
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </TabsContent>
                            
                            <TabsContent value="documents">
                              <div className="grid grid-cols-3 gap-4">
                                {/* CPR */}
                                <div className="p-3 bg-white rounded-lg border">
                                  <div className="flex items-center justify-between mb-2">
                                    <h4 className="font-medium text-sm flex items-center gap-2">
                                      <Heart className="w-4 h-4 text-red-500" />
                                      CPR Certification
                                    </h4>
                                    {getStatusBadge(staff.cpr?.status)}
                                  </div>
                                  <p className="text-xs text-muted-foreground">Current CPR/First Aid certification required</p>
                                </div>
                                
                                {/* Mandated Reporter */}
                                <div className="p-3 bg-white rounded-lg border">
                                  <div className="flex items-center justify-between mb-2">
                                    <h4 className="font-medium text-sm flex items-center gap-2">
                                      <Shield className="w-4 h-4 text-amber-500" />
                                      Mandated Reporter
                                    </h4>
                                    {getStatusBadge(staff.mandated_reporter?.status)}
                                  </div>
                                  <p className="text-xs text-muted-foreground">Child abuse recognition & reporting training</p>
                                </div>
                                
                                {/* High School Diploma */}
                                <div className="p-3 bg-white rounded-lg border">
                                  <div className="flex items-center justify-between mb-2">
                                    <h4 className="font-medium text-sm flex items-center gap-2">
                                      <GraduationCap className="w-4 h-4 text-indigo-500" />
                                      High School Diploma
                                    </h4>
                                    {getStatusBadge(staff.high_school_diploma?.status)}
                                  </div>
                                  <p className="text-xs text-muted-foreground">HS diploma or GED verification</p>
                                </div>
                                
                                {/* WI Registry */}
                                <div className="p-3 bg-white rounded-lg border">
                                  <div className="flex items-center justify-between mb-2">
                                    <h4 className="font-medium text-sm flex items-center gap-2">
                                      <Award className="w-4 h-4 text-teal-500" />
                                      WI Registry Certificate
                                    </h4>
                                    {getStatusBadge(staff.registry_certificate?.status)}
                                  </div>
                                  <p className="text-xs text-muted-foreground">Wisconsin Registry certification</p>
                                </div>
                                
                                {/* Background Check */}
                                <div className="p-3 bg-white rounded-lg border">
                                  <div className="flex items-center justify-between mb-2">
                                    <h4 className="font-medium text-sm flex items-center gap-2">
                                      <Shield className="w-4 h-4 text-blue-500" />
                                      Background Check
                                    </h4>
                                    {getStatusBadge(staff.background_check?.status)}
                                  </div>
                                  <p className="text-xs text-muted-foreground">Approved DCF background check</p>
                                </div>
                                
                                {/* Health Report */}
                                <div className="p-3 bg-white rounded-lg border">
                                  <div className="flex items-center justify-between mb-2">
                                    <h4 className="font-medium text-sm flex items-center gap-2">
                                      <BookOpen className="w-4 h-4 text-pink-500" />
                                      Staff Health Report
                                    </h4>
                                    {getStatusBadge(staff.health_report?.status)}
                                  </div>
                                  <p className="text-xs text-muted-foreground">Physical exam & TB test documentation</p>
                                </div>
                              </div>
                            </TabsContent>
                          </Tabs>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            
            {sortedStaff.length === 0 && (
              <div className="p-8 text-center text-muted-foreground">
                {searchQuery ? 'No staff members match your search' : 'No staff members found'}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FormComplianceDashboard;
